import { useState } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import MenuItem from "@mui/material/MenuItem";
import TextField from "@mui/material/TextField";
import type { FilterField } from "../modules/reports/catalog";
import type { ReportFilter } from "../api/reports";

export type FilterValues = Omit<ReportFilter, "namespace_prefix">;

const LABEL: Record<FilterField, string> = {
  start: "From",
  end: "To",
  username: "Username",
  mobile: "Mobile",
  group_by: "Per",
};

/**
 * 🔑 There is no namespace field here, and there must never be one. The tenant
 * comes from the token; a filter the operator can type is a tenant the
 * operator can choose.
 *
 * The form does not auto-apply. A report is a database query over a date
 * range, and re-running it on every keystroke would put a load on the backend
 * proportional to typing speed.
 */
/**
 * YYYY-MM-DD, which is what <input type="date"> expects, as the operator's OWN
 * calendar day. toISOString() gave the UTC day, so between 00:00 and 05:30 IST
 * "today" was yesterday.
 */
export function isoDay(d: Date): string {
  const p = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

/**
 * Quick ranges (owner 2026-10-24: "last 30 days or 60 days or 180 days or week
 * etc."). Each ends today and includes today; the months are calendar months.
 * radreportsapi bounds any window to 366 days, so a year is the longest.
 */
export const PRESETS: {
  label: string;
  range: (today: Date) => { start: string; end: string };
}[] = [
  { label: "Last 7 days", range: (t) => lastDays(t, 7) },
  { label: "Last 30 days", range: (t) => lastDays(t, 30) },
  { label: "Last 60 days", range: (t) => lastDays(t, 60) },
  { label: "Last 90 days", range: (t) => lastDays(t, 90) },
  { label: "Last 180 days", range: (t) => lastDays(t, 180) },
  { label: "Last 365 days", range: (t) => lastDays(t, 365) },
  {
    label: "This month",
    range: (t) => ({
      start: isoDay(new Date(t.getFullYear(), t.getMonth(), 1)),
      end: isoDay(t),
    }),
  },
  {
    label: "Last month",
    range: (t) => ({
      start: isoDay(new Date(t.getFullYear(), t.getMonth() - 1, 1)),
      end: isoDay(new Date(t.getFullYear(), t.getMonth(), 0)),
    }),
  },
];

function lastDays(today: Date, n: number): { start: string; end: string } {
  return {
    start: isoDay(new Date(today.getFullYear(), today.getMonth(), today.getDate() - (n - 1))),
    end: isoDay(today),
  };
}

/**
 * A date range is pre-filled so the form is usable on arrival rather than
 * being a blank thing to guess at. Thirty days is the product default; it is
 * deliberately NOT tuned to whatever the current dataset happens to contain,
 * because a default chosen to make test data appear would mislead in
 * production. The applied range is shown with the results instead, so an empty
 * answer is interpretable either way.
 */
function defaults(fields: FilterField[]): Record<string, string> {
  const out: Record<string, string> = fields.includes("group_by") ? { group_by: "day" } : {};
  if (!fields.includes("start") && !fields.includes("end")) return out;
  return { ...out, ...lastDays(new Date(), 30) };
}

export function FilterBar({
  fields,
  onApply,
  busy,
  required = [],
}: {
  fields: FilterField[];
  onApply: (v: FilterValues) => void;
  busy?: boolean;
  required?: FilterField[];
}) {
  const [draft, setDraft] = useState<Record<string, string>>(() => defaults(fields));
  if (fields.length === 0) return null;

  const missing = required.filter((f) => !draft[f]?.trim());

  const set = (k: string, v: string) => setDraft((d) => ({ ...d, [k]: v }));

  const apply = (values: Record<string, string> = draft) => {
    const out: FilterValues = {};
    for (const f of fields) {
      const raw = values[f]?.trim();
      if (!raw) continue;
      if (f === "start" || f === "end") {
        // The Go side decodes these into *time.Time, so they must be RFC3339.
        // A date input yields YYYY-MM-DD; widen it to cover the whole day, with
        // `end` at the last instant rather than midnight — otherwise "to the
        // 5th" silently excludes everything that happened on the 5th.
        out[f] = f === "start" ? `${raw}T00:00:00Z` : `${raw}T23:59:59Z`;
      } else if (f === "group_by") {
        out.group_by = raw === "month" ? "month" : "day";
      } else {
        out[f] = raw;
      }
    }
    onApply(out);
  };

  const hasDates = fields.includes("start") && fields.includes("end");
  const pick = (range: { start: string; end: string }) => {
    const next = { ...draft, ...range };
    setDraft(next);
    apply(next);
  };

  return (
    <>
      {hasDates && (
        <Box sx={{ display: "flex", gap: 1, mb: 1.5, flexWrap: "wrap" }} aria-label="Quick date ranges">
          {PRESETS.map((p) => {
            const r = p.range(new Date());
            const on = draft.start === r.start && draft.end === r.end;
            return (
              <Chip
                key={p.label}
                label={p.label}
                size="small"
                color={on ? "primary" : "default"}
                variant={on ? "filled" : "outlined"}
                onClick={() => pick(r)}
                disabled={busy}
              />
            );
          })}
        </Box>
      )}
      <Box
        component="form"
        onSubmit={(e) => {
          e.preventDefault();
          apply();
        }}
        sx={{
          display: "flex",
          gap: 2,
          mb: 2,
          flexWrap: "wrap",
          alignItems: "center",
        }}
      >
        {fields.map((f) =>
          f === "group_by" ? (
            <TextField
              key={f}
              select
              size="small"
              label={LABEL[f]}
              value={draft[f] ?? "day"}
              onChange={(e) => set(f, e.target.value)}
              sx={{ minWidth: 110 }}
            >
              <MenuItem value="day">Day</MenuItem>
              <MenuItem value="month">Month</MenuItem>
            </TextField>
          ) : (
            <TextField
              key={f}
              size="small"
              label={LABEL[f]}
              type={f === "start" || f === "end" ? "date" : "text"}
              slotProps={{ inputLabel: { shrink: true } }}
              value={draft[f] ?? ""}
              onChange={(e) => set(f, e.target.value)}
              required={required.includes(f)}
              error={required.includes(f) && !draft[f]?.trim()}
            />
          ),
        )}
        <Button type="submit" variant="contained" size="medium" disabled={busy || missing.length > 0}>
          Run report
        </Button>
      </Box>
    </>
  );
}
